import { Bike, CircleCheck, CircleDashed, TriangleAlert } from 'lucide-react';
import {
  DISPLAY_STATES,
  type DisplayState,
  GAP_STYLE,
  HOLD_STATE,
  STYLE,
  WORD,
} from '../timeline/barStyle.js';
import type { ScheduleView } from '../timeline/dropLogic.js';
import type { BarState } from '../types.js';

/**
 * 096 (FR-009), 316 (FR-017) — what the bars mean. 329 (US3, FR-005): no longer on the page; this is
 * the Guide's "What the colours mean" tab, with the same entries and test ids.
 *
 * 🔴 **Generated from the same map the bars are drawn from** (`barStyle.ts`), never from a second
 * literal list. A hand-maintained legend drifts the moment a state is added, and the drift is
 * silent: the screen keeps rendering a bar nobody can explain.
 *
 * 316: one entry per display state (ten), including the four non-booking kinds, which before 096
 * all shared the word `Blocked`.
 */

export interface LegendEntry {
  key: DisplayState;
  /** The hold state this entry belongs to. */
  state: BarState;
  word: string;
  style: string;
}

export const LEGEND_ENTRIES: LegendEntry[] = DISPLAY_STATES.map((key) => ({
  key,
  state: HOLD_STATE[key],
  word: WORD[key],
  style: STYLE[key],
}));

const HEADING = 'm-0 text-[11px] font-semibold uppercase tracking-[1px] text-steel';

/**
 * #631 — the marks drawn on bars and the day tints, explained next to the states. The same three
 * icons as `BarItem`; the tints only in Week and Month, the only views that draw them.
 */
export function LegendContent({ view = 'day' }: { view?: ScheduleView }) {
  return (
    <div data-testid="schedule-legend" className="flex flex-col gap-5 text-[13px] text-charcoal">
      <section className="flex flex-col gap-2">
        <h3 className={HEADING}>Bars</h3>
        <ul
          aria-label="Schedule legend"
          className="m-0 grid list-none grid-cols-2 gap-x-4 gap-y-2.5 p-0"
        >
          {LEGEND_ENTRIES.map((entry) => (
            <li key={entry.key} className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className={['inline-block h-3 w-5 shrink-0 rounded-sm', entry.style].join(' ')}
              />
              {entry.word}
            </li>
          ))}
        </ul>
      </section>

      <section className="flex flex-col gap-2">
        <h3 className={HEADING}>Marks on a bar</h3>
        <ul aria-label="Marks on a bar" className="m-0 flex list-none flex-col gap-2 p-0">
          <li data-testid="schedule-legend-attention" className="flex items-center gap-2">
            <TriangleAlert aria-hidden="true" size={14} />
            Needs attention (overdue, abandoned?, no hold)
          </li>
          <li data-testid="schedule-legend-paid" className="flex items-center gap-2">
            <CircleCheck aria-hidden="true" size={14} />
            Paid
          </li>
          <li data-testid="schedule-legend-unpaid" className="flex items-center gap-2">
            <CircleDashed aria-hidden="true" size={14} />
            Not paid
          </li>
          {/* 361 (design 2d): the food-delivery mark; the word "Delivery" shows on wide bars. */}
          <li data-testid="schedule-legend-delivery" className="flex items-center gap-2">
            <Bike aria-hidden="true" size={14} />
            OK to do food delivery
          </li>
          {/* 386 (#1278): the shade after a rental. */}
          <li data-testid="schedule-legend-gap" className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className={['inline-block h-3 w-5 shrink-0 rounded-sm', GAP_STYLE].join(' ')}
            />
            Gap between rentals (customers cannot book here)
          </li>
        </ul>
      </section>

      {/* 317 (FR-017): what the two figures mean, one sentence each. */}
      <section className="flex flex-col gap-2">
        <h3 className={HEADING}>Figures</h3>
        <ul aria-label="Figures" className="m-0 flex list-none flex-col gap-2 p-0 leading-normal">
          <li data-testid="schedule-legend-booked">
            % booked: share of the car's unblocked time held by confirmed, checked-out, checked-in
            or completed rentals
          </li>
          <li data-testid="schedule-legend-free">Free: no booking or block in that column</li>
        </ul>
      </section>

      {view !== 'day' && (
        <section className="flex flex-col gap-2">
          <h3 className={HEADING}>Days</h3>
          <ul aria-label="Days" className="m-0 flex list-none gap-5 p-0">
            <li data-testid="schedule-legend-weekend" className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className="inline-block h-3 w-5 rounded-sm border border-hairline bg-surface-soft"
              />
              Weekend
            </li>
            <li data-testid="schedule-legend-today" className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className="inline-block h-3 w-5 rounded-sm border border-hairline bg-surface-feature"
              />
              Today
            </li>
          </ul>
        </section>
      )}
    </div>
  );
}
